import React, { useEffect, useRef, useState } from 'react';
import { Alert, Badge, Form, Spinner } from 'react-bootstrap';
import { InvoicePaymentMap, PaymentValidationResult, ValidatedInvoice } from '../../utils/paymentTypeUtils';

interface PaymentTypeValidatorProps {
  validationResult?: PaymentValidationResult;
  isLoading: boolean;
  invoicePayments: InvoicePaymentMap;
  onAmountChange: (invoiceNumber: string, amount: number, shouldValidate?: boolean) => void;
}

interface AmountInputProps {
  invoice: ValidatedInvoice;
  invoicePayments: InvoicePaymentMap;
  onAmountChange: (invoiceNumber: string, amount: number, shouldValidate?: boolean) => void;
}

// Local string state so the user's typing is not fought by toFixed(2)
// reformatting on every render. Only commits a parsed number to the
// parent via onChange (without triggering validation) and onBlur (with
// validation). This also strips the float-precision noise that ePay
// amounts sometimes pick up (e.g. 29562.810546875).
const AmountInput: React.FC<AmountInputProps> = ({ invoice, invoicePayments, onAmountChange }) => {
  const formatForDisplay = (value: number) => (Number.isFinite(value) ? value.toFixed(2) : '');
  const liveValue = invoicePayments[invoice.invoiceNumber] ?? invoice.requestedAmount;
  const [draft, setDraft] = useState<string>(formatForDisplay(liveValue));
  const focusedRef = useRef(false);

  // Sync external updates (validation echo, invoice swap) only when the
  // user is not actively editing — otherwise a re-render mid-type would
  // overwrite their input.
  useEffect(() => {
    if (!focusedRef.current) {
      setDraft(formatForDisplay(liveValue));
    }
  }, [liveValue]);

  return (
    <Form.Control
      type="number"
      min={invoice.minAmount}
      max={invoice.maxAmount}
      step={0.01}
      value={draft}
      onFocus={() => { focusedRef.current = true; }}
      onChange={(event) => {
        const raw = event.target.value;
        setDraft(raw);
        const value = parseFloat(raw);
        onAmountChange(invoice.invoiceNumber, isNaN(value) ? 0 : value, false);
      }}
      onBlur={(event) => {
        focusedRef.current = false;
        const value = parseFloat(event.target.value || '0');
        const safe = isNaN(value) ? 0 : value;
        setDraft(formatForDisplay(safe));
        onAmountChange(invoice.invoiceNumber, safe, true);
      }}
    />
  );
};

const renderInvoiceRow = (
  invoice: ValidatedInvoice,
  invoicePayments: InvoicePaymentMap,
  onAmountChange: (invoiceNumber: string, amount: number, shouldValidate?: boolean) => void
) => {
  const canEditAmount = invoice.allowsPartial && !invoice.isSmallBalance;

  return (
    <tr key={`${invoice.invoiceNumber}-${invoice.requestedAmount}`}>
      <td>{invoice.invoiceNumber}</td>
      <td>{invoice.dueDate}</td>
      <td>${invoice.outstanding.toFixed(2)}</td>
      <td>
        <Badge bg={invoice.type === 'installment' ? 'success' : invoice.type === 'audit' || invoice.type === 'endorsement' ? 'warning' : 'primary'}>
          {invoice.type.replace('_', ' ')}
        </Badge>
      </td>
      <td style={{ minWidth: '140px' }}>
        {canEditAmount ? (
          <AmountInput
            invoice={invoice}
            invoicePayments={invoicePayments}
            onAmountChange={onAmountChange}
          />
        ) : (
          `$${invoice.requestedAmount.toFixed(2)}`
        )}
      </td>
    </tr>
  );
};

const PaymentTypeValidator: React.FC<PaymentTypeValidatorProps> = ({ validationResult, isLoading, invoicePayments, onAmountChange }) => {
  if (isLoading) {
    return (
      <div className="payment-type-validator text-center mb-3">
        <Spinner animation="border" size="sm" /> Validating selection…
      </div>
    );
  }

  if (!validationResult) {
    return null;
  }

  const { isValid, errors, warnings, invoices } = validationResult;

  return (
    <div className="payment-type-validator mb-3">
      {!isValid && errors && (
        <Alert variant="danger">
          <strong>Payment Error</strong>
          <ul className="mb-0 mt-1">
            {errors.map((error, index) => (
              <li key={index}>{error}</li>
            ))}
          </ul>
        </Alert>
      )}

      {warnings && warnings.length > 0 && (
        <Alert variant="warning">
          <strong>Important</strong>
          <ul className="mb-0 mt-1">
            {warnings.map((warning, index) => (
              <li key={index}>{warning}</li>
            ))}
          </ul>
        </Alert>
      )}

      {invoices && invoices.length > 0 && (
        <div className="invoice-details mb-2">
          <h6>Selected Invoice{invoices.length > 1 ? 's' : ''}</h6>
          <div className="table-responsive">
            <table className="table table-sm">
              <thead>
                <tr>
                  <th>Invoice #</th>
                  <th>Due Date</th>
                  <th>Outstanding</th>
                  <th>Type</th>
                  <th>Amount To Pay</th>
                </tr>
              </thead>
              <tbody>
                {invoices.map((invoice) => renderInvoiceRow(invoice, invoicePayments, onAmountChange))}
            </tbody>
          </table>
        </div>
      </div>
      )}
    </div>
  );
};

export default PaymentTypeValidator;
